import styled, { keyframes } from "styled-components/macro";
import { themeVariables } from "../../../themeVariables";

/**
 * Outer container — stretches to the full height of the flex parent
 * (the editor-container-wrapper) so the sticky inner element has
 * the full document height as its containing block.
 * overflow must be visible for position: sticky to work on the child.
 */
export const SidebarContainer = styled.div<{ $width: number }>`
  min-width: ${({ $width }) => $width}px;
  max-width: ${({ $width }) => $width}px;
  width: ${({ $width }) => $width}px;
  flex: 0 0 ${({ $width }) => $width}px;
  align-self: stretch;
  order: 2;
  position: relative;
  /* Ensure the containing block is always tall enough for the sticky
     SidebarInner (which uses margin-top: -200px) to position correctly,
     even when the editor iframe content is very short. */
  min-height: 100vh;
`;

/**
 * Inner viewport — uses CSS sticky to stay visible below the toolbar
 * as the editor-stack scrolls. Flex column layout keeps the header
 * pinned at top while the scrollable area fills remaining space.
 */
export const SidebarInner = styled.div`
  position: sticky;
  top: 48px;
  margin-top: -200px;
  height: calc(100vh - 108px);
  display: flex;
  flex-direction: column;
  background-color: ${themeVariables.menuColor};
  background-image: linear-gradient(${themeVariables.containerColor}, ${themeVariables.containerColor});
  border-radius: 0 0 20px 0;
`;

export const SidebarHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2px;
  padding: 8px 8px;
  flex-shrink: 0;
  z-index: 1;
  color: ${themeVariables.grey0};
  background: transparent;

  .comment-count-text {
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    color: ${themeVariables.grey0};
  }
`;

export const CommentFilterSelectWrapper = styled.div`
  position: relative;
  width: 156px;
  flex: 0 0 156px;
  flex-shrink: 0;
`;

export const CommentFilterTriggerButton = styled.button`
  all: unset;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 16px;
  background: #f3f3f3;
  color: #2e2e2e;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  padding: 8px 10px;
  cursor: pointer;
  width: 156px;
  justify-content: flex-start;
  white-space: nowrap;

  .trigger-label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:focus {
    outline: 2px solid ${themeVariables.primaryCTA};
    outline-offset: 1px;
  }

  &:hover {
    background: #efefef;
  }

  svg {
    width: 16px;
    height: 16px;
    margin-left: auto;
    flex-shrink: 0;
    display: block;

    path {
      fill: #4a4a4a;
    }
  }

  &.use-component-styles {
    border-radius: 10px;
    border: 1px solid var(--grey-3);
    background: var(--menu-color);
    color: var(--grey-0);

    svg {
      path {
        fill: var(--grey-0);
      }
    }

    &:hover {
      background: var(--menu-color-hover);
    }
  }
`;

export const CommentFilterCountPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  flex-shrink: 0;
  padding: 2px 6px;
  border-radius: 8px;
  background: #e5e5e5;
  color: #3f3f3f;
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
`;

export const CommentFilterDropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  width: 100%;
  min-width: 160px;
  padding: 4px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #f7f7f7;
  box-shadow: ${themeVariables.lightDropShadow};
  z-index: 20;
`;

export const CommentFilterOptionButton = styled.button<{ $active?: boolean }>`
  all: unset;
  box-sizing: border-box;
  width: 100%;
  background: ${({ $active }) => ($active ? "#ededed" : "transparent")};
  color: #2e2e2e;
  border-radius: 8px;
  padding: 6px 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: ${themeVariables.menuColorHover};
    color: var(--grey-0);
  }
`;

export const CommentFilterOptionLabel = styled.span`
  font-size: 14px;
  line-height: 20px;
`;

/**
 * Scrollable area below the header — takes remaining space via flex: 1.
 * Scrollbar is hidden but scroll functionality is preserved.
 */
export const ScrollableCardArea = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 10px 12px 0;

  /* Hide scrollbar but keep scroll functionality */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Chrome / Safari */
  }
`;

export const NavButton = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  flex-shrink: 0;
  opacity: ${({ $disabled }) => ($disabled ? 0.3 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};

  &:hover {
    background: ${({ $disabled }) => ($disabled ? "transparent" : themeVariables.menuColorHover)};
  }

  svg {
    width: 16px;
    height: 16px;

    path {
      fill: ${themeVariables.grey0};
    }
  }
`;

export const CardListContainer = styled.div`
  display: block;
  padding-bottom: 16px;
`;

const paginationSpinner = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

export const PaginationLoadingContainer = styled.div`
  min-height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: ${themeVariables.grey2};
  font-size: 13px;
  line-height: 18px;
`;

export const PaginationSpinner = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 2px solid ${themeVariables.grey4};
  border-top-color: ${themeVariables.grey1};
  animation: ${paginationSpinner} 0.8s linear infinite;
`;

export const EmptyStateContainer = styled.div`
  min-height: 260px;
  flex-direction: column;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: ${themeVariables.grey1};
`;

export const EmptyStateMessage = styled.p`
  margin: 0;
  max-width: 220px;
  font-size: 18px;
  line-height: 26px;
  font-weight: 400;
  color: ${themeVariables.grey1};
`;
